import { PAGE_FREQUENCIES, PAGE_SECONDS_DEFAULT, PAGE_URL_OPERATORS, UNRESPONSIVE_MINUTES_DEFAULT, WORKFLOW_LIMITS, type PageFrequency, type PageUrlOperator, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Select } from "@sagot/ui";
import { IntegerInput } from "../../../workspace/shared";
import type { PageUrlRule, TriggerOptions } from "../types";
import { PlatformEventOptions } from "./system/platform-event-options";
import { ClickFrequencyField, ClickSelectorField, TriggerProblems, WatchedAttributeField, WidgetOnlyNote } from "./trigger-options-p5";

const O = "engage.workflows.builder.options";

interface EditorProps {
  options: TriggerOptions;
  disabled: boolean;
  onChange: (options: TriggerOptions) => void;
}

/** Triggers that wait for silence: after how many minutes without a reply they start. */
function MinutesOption({ trigger, options, disabled, onChange }: EditorProps & { trigger: WorkflowTrigger }) {
  const { t } = useTranslation();
  return (
    <Field label={t(`${O}.minutes`)} hint={`${t(`${O}.minutesHint.${trigger}`)} ${t(`${O}.minutesRange`, { max: WORKFLOW_LIMITS.unresponsiveMinutesMax })}`}>
      <IntegerInput className="w-32" min={1} max={WORKFLOW_LIMITS.unresponsiveMinutesMax} disabled={disabled} value={options.minutes ?? UNRESPONSIVE_MINUTES_DEFAULT} onChange={(minutes) => { onChange({ ...options, minutes }); }} />
    </Field>
  );
}

function UrlRules({ options, disabled, onChange }: EditorProps) {
  const { t } = useTranslation();
  const rules = options.url_rules ?? [];
  const setRules = (url_rules: PageUrlRule[]) => { onChange({ ...options, url_rules }); };
  const setRule = (index: number, patch: Partial<PageUrlRule>) => { setRules(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))); };
  return (
    <fieldset className="space-y-2">
      <legend className="label">{t(`${O}.urlRules`)}</legend>
      <p className="hint">{t(`${O}.urlRulesHint`)}</p>
      {rules.map((rule, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <Select aria-label={t(`${O}.ruleOperator`, { n: index + 1 })} className="h-9 w-auto text-xs" disabled={disabled} value={rule.op} onChange={(e) => { setRule(index, { op: e.target.value as PageUrlOperator }); }}>
            {PAGE_URL_OPERATORS.map((op) => <option key={op} value={op}>{t(`${O}.operators.${op}`)}</option>)}
          </Select>
          <Input aria-label={t(`${O}.ruleValue`, { n: index + 1 })} className="h-9 min-w-40 flex-1 text-xs" maxLength={WORKFLOW_LIMITS.urlRuleValue} placeholder={t(`${O}.urlPlaceholder`)} disabled={disabled} value={rule.value} onChange={(e) => { setRule(index, { value: e.target.value }); }} />
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${O}.removeRule`, { n: index + 1 })} onClick={() => { setRules(rules.filter((_, i) => i !== index)); }}>
            <Icon name="trash" />
          </Button>
        </div>
      ))}
      {rules.length < WORKFLOW_LIMITS.urlRules ? (
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { setRules([...rules, { op: "contains", value: "" }]); }}>
          <Icon name="plus" />
          {t(`${O}.addRule`)}
        </Button>
      ) : null}
    </fieldset>
  );
}

/** Visitor visits a page: which URLs, how long on the page, how often the message shows. */
function PageVisitOptions(props: EditorProps) {
  const { t } = useTranslation();
  const { options, disabled, onChange } = props;
  return (
    <div className="space-y-4">
      <UrlRules {...props} />
      <Field label={t(`${O}.secondsOnPage`)} hint={t(`${O}.secondsOnPageHint`)}>
        <IntegerInput className="w-32" min={0} max={WORKFLOW_LIMITS.secondsOnPageMax} disabled={disabled} value={options.seconds_on_page ?? PAGE_SECONDS_DEFAULT} onChange={(seconds_on_page) => { onChange({ ...options, seconds_on_page }); }} />
      </Field>
      <Field label={t(`${O}.frequency`)}>
        <Select disabled={disabled} value={options.frequency ?? "once"} onChange={(e) => { onChange({ ...options, frequency: e.target.value as PageFrequency }); }}>
          {PAGE_FREQUENCIES.map((frequency) => <option key={frequency} value={frequency}>{t(`${O}.frequencies.${frequency}`)}</option>)}
        </Select>
      </Field>
    </div>
  );
}

/** Visitor clicks an element: which element, on which pages, how often. Website widget only. */
function ClickOptions(props: EditorProps) {
  return (
    <div className="space-y-4">
      <WidgetOnlyNote />
      <ClickSelectorField {...props} />
      <UrlRules {...props} />
      <ClickFrequencyField {...props} />
      <TriggerProblems />
    </div>
  );
}

/** The options of triggers that have any, or nothing. A custom event only gets a hint (the condition names the event). */
export function TriggerOptionsEditor({ trigger, options, disabled, onChange }: EditorProps & { trigger: WorkflowTrigger }) {
  const { t } = useTranslation();
  if (trigger === "visitor_unresponsive" || trigger === "staff_unresponsive") return <MinutesOption trigger={trigger} options={options} disabled={disabled} onChange={onChange} />;
  if (trigger === "visitor_visits_page") return <PageVisitOptions options={options} disabled={disabled} onChange={onChange} />;
  if (trigger === "visitor_clicks_element") return <ClickOptions options={options} disabled={disabled} onChange={onChange} />;
  if (trigger === "attribute_changed") {
    return (
      <div className="space-y-3">
        <WatchedAttributeField options={options} disabled={disabled} onChange={onChange} />
        <TriggerProblems />
      </div>
    );
  }
  if (trigger === "visitor_event") return <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="event-hint">{t(`${O}.eventHint`)}</p>;
  if (trigger === "platform_event") return <PlatformEventOptions options={options} disabled={disabled} onChange={onChange} />;
  return null;
}

/** Whether a trigger has anything to set in its options block. */
export const hasTriggerOptions = (trigger: WorkflowTrigger): boolean =>
  trigger === "visitor_unresponsive" || trigger === "staff_unresponsive" || trigger === "visitor_visits_page" || trigger === "visitor_event" || trigger === "visitor_clicks_element" || trigger === "attribute_changed" || trigger === "platform_event";
